/**
 * Student picker modal for a field event.
 */
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UserPlus, UserCheck } from 'lucide-react';
import { SlidePanel } from '@/components/common/SlidePanel';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import { CreateStudentView } from '@/components/students/inperson/CreateStudentView';
import { cn } from '@/lib/utils';
import {
  UnregisteredStudentForm,
  type UnregisteredStudentData,
  type UnregisteredStudentFormHandle,
} from './UnregisteredStudentForm';
import { prependUserToUsersSearchCaches } from '@/utils/created-user-profile-utils';
import {
  EVENT_STUDENT_CREATION_OPTIONS,
  type EventStudentCreationType,
} from '@/components/events/event-configs';
import type { UserWithProfile } from '@/types/user.types';

/** Props for {@link EventStudentPickerModal}. */
interface EventStudentPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStudentCreated: (user: UserWithProfile) => void;
  onUnregisteredAdded: (data: UnregisteredStudentData) => void;
}

/** Student picker modal for a field event. */
export const EventStudentPickerModal: React.FC<EventStudentPickerModalProps> = ({
  open,
  onOpenChange,
  onStudentCreated,
  onUnregisteredAdded,
}) => {
  const queryClient = useQueryClient();
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);
  const [isStudentPanelOpen, setIsStudentPanelOpen] = React.useState(false);
  const [isUnregisteredModalOpen, setIsUnregisteredModalOpen] = React.useState(false);
  const unregisteredFormRef = React.useRef<UnregisteredStudentFormHandle>(null);
  /** Guard so opening the picker only mounts the type modal once per `open` cycle. */
  const hasStartedFlowRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      hasStartedFlowRef.current = false;
      return;
    }
    if (hasStartedFlowRef.current) return;
    hasStartedFlowRef.current = true;
    setIsPickerOpen(true);
  }, [open]);

  /** Registered students open the create panel; walk-ins open {@link UnregisteredStudentForm}. */
  const handleTypeSelect = (type: EventStudentCreationType) => {
    setIsPickerOpen(false);
    if (type === 'registered') {
      setIsStudentPanelOpen(true);
    } else {
      setIsUnregisteredModalOpen(true);
    }
  };

  /** Closing the type picker also closes the parent-controlled `open`. */
  const handlePickerClose = () => {
    setIsPickerOpen(false);
    onOpenChange(false);
  };

  /** Tear down the registered-student create panel and notify the parent. */
  const handleStudentPanelClose = () => {
    setIsStudentPanelOpen(false);
    onOpenChange(false);
  };

  /** Seed the users-search cache so the new student appears immediately. */
  const handleStudentCreated = (created: UserWithProfile) => {
    prependUserToUsersSearchCaches(queryClient, 'student', created);
    onStudentCreated(created);
    handleStudentPanelClose();
  };

  /** Tear down the walk-in form and notify the parent. */
  const handleUnregisteredModalClose = () => {
    setIsUnregisteredModalOpen(false);
    onOpenChange(false);
  };

  return (
    <>
      <BaseModal
        open={isPickerOpen}
        onClose={handlePickerClose}
        size="md"
        header={{
          icon: UserCheck,
          iconSize: 'md',
          title: 'Ajouter un élève',
          subtitle: "Choisissez le type d'élève à ajouter",
          titleSize: 'lg',
          titleWeight: 'semibold',
        }}
        hideContentWrapper
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          {EVENT_STUDENT_CREATION_OPTIONS.map((option) => {
            const Icon = option.Icon;
            return (
              <button
                key={option.type}
                type="button"
                onClick={() => handleTypeSelect(option.type)}
                className={cn(
                  'group relative flex flex-col items-start gap-3 rounded-2xl border-2 p-5 text-left transition-all',
                  'hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2',
                )}
                style={{
                  borderColor: 'var(--color-1-nuance-3)',
                  background: 'color-mix(in srgb, var(--color-1-nuance-3) 3%, transparent)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = option.accentColor;
                  e.currentTarget.style.background = `color-mix(in srgb, ${option.accentColor} 8%, transparent)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-1-nuance-3)';
                  e.currentTarget.style.background =
                    'color-mix(in srgb, var(--color-1-nuance-3) 3%, transparent)';
                }}
              >
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-xl border-2 transition-all"
                  style={{
                    borderColor: option.accentColor,
                    background: `color-mix(in srgb, ${option.accentColor} 10%, transparent)`,
                  }}
                >
                  <Icon className="h-6 w-6" style={{ color: option.accentColor }} />
                </div>
                <div>
                  <h3
                    className="text-base font-semibold"
                    style={{ color: 'var(--color-1-nuance-1)' }}
                  >
                    {option.label}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{option.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </BaseModal>

      <SlidePanel isOpen={isStudentPanelOpen} onClose={handleStudentPanelClose}>
        <CreateStudentView onClose={handleStudentPanelClose} onSuccess={handleStudentCreated} />
      </SlidePanel>

      <BaseModal
        open={isUnregisteredModalOpen}
        onClose={handleUnregisteredModalClose}
        size="md"
        header={{
          icon: UserPlus,
          iconSize: 'md',
          title: 'Ajouter un élève non inscrit',
          subtitle: "Renseignez les informations de l'élève",
          titleSize: 'lg',
          titleWeight: 'semibold',
        }}
        footer={
          <>
            <Button preset="modal-cancel" onClick={handleUnregisteredModalClose}>
              Annuler
            </Button>
            <Button preset="modal-confirm" onClick={() => unregisteredFormRef.current?.submit()}>
              Ajouter
            </Button>
          </>
        }
      >
        <UnregisteredStudentForm
          ref={unregisteredFormRef}
          onAdd={(data) => {
            onUnregisteredAdded(data);
            handleUnregisteredModalClose();
          }}
        />
      </BaseModal>
    </>
  );
};
